import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  CircleX,
  Clock3,
  ListTodo,
  Plus,
  RefreshCw,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import TaskActionButtons, {
  type TaskAction,
} from "./TaskActionButtons";

import {
  formatSamaraDateTimeShort,
} from "./timezone";

import type {
  Project,
  Task,
  TaskPriority,
  TaskStatus,
} from "./types";

type StatusFilter =
  | "all"
  | TaskStatus;

type PriorityFilter =
  | "all"
  | TaskPriority;

const STATUS_LABELS: Record<
  TaskStatus,
  string
> = {
  pending: "Ожидает",
  in_progress: "В работе",
  waiting: "На паузе",
  completed: "Выполнена",
  failed: "Провалена",
};

const PRIORITY_LABELS: Record<
  TaskPriority,
  string
> = {
  low: "Низкий",
  normal: "Обычный",
  high: "Высокий",
  urgent: "Срочный",
};

function formatDuration(
  totalSeconds: number,
): string {
  const seconds = Math.max(
    0,
    Math.floor(totalSeconds),
  );

  const hours = Math.floor(
    seconds / 3600,
  );

  const minutes = Math.floor(
    (seconds % 3600) / 60,
  );

  const remainingSeconds =
    seconds % 60;

  if (hours > 0) {
    return [
      String(hours).padStart(2, "0"),
      String(minutes).padStart(2, "0"),
      String(remainingSeconds)
        .padStart(2, "0"),
    ].join(":");
  }

  return [
    String(minutes).padStart(2, "0"),
    String(remainingSeconds)
      .padStart(2, "0"),
  ].join(":");
}

function AllTasksPanel({
  tasks,
  projects,
  isLoading,
  error,
  searchQuery,
  taskActionInProgress,
  onSearchChange,
  onRefresh,
  onCreateTask,
  onOpenTask,
  onTaskAction,
  onClose,
}: {
  tasks: Task[];
  projects: Project[];

  isLoading: boolean;
  error: string | null;
  searchQuery: string;

  taskActionInProgress: {
    taskId: number;
    action: TaskAction;
  } | null;

  onSearchChange:
    (value: string) => void;

  onRefresh:
    () => Promise<void>;

  onCreateTask:
    () => void;

  onOpenTask:
    (task: Task) => void;

  onTaskAction: (
    task: Task,
    action: TaskAction,
  ) => Promise<void>;

  onClose:
    () => void;
}) {
  const [statusFilter, setStatusFilter] =
    useState<StatusFilter>("all");

  const [
    priorityFilter,
    setPriorityFilter,
  ] = useState<PriorityFilter>("all");

  const [
    projectFilter,
    setProjectFilter,
  ] = useState("all");

  useEffect(() => {
    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    function handleKeyDown(
      event: KeyboardEvent,
    ): void {
      if (event.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener(
      "keydown",
      handleKeyDown,
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [onClose]);

  const normalizedSearch =
    searchQuery
      .trim()
      .toLocaleLowerCase("ru-RU");

  const filteredTasks = useMemo(
    () => tasks.filter((task) => {
      if (
        statusFilter !== "all"
        && task.status !== statusFilter
      ) {
        return false;
      }

      if (
        priorityFilter !== "all"
        && task.priority !== priorityFilter
      ) {
        return false;
      }

      if (
        projectFilter !== "all"
        && (
          projectFilter === "none"
            ? task.project !== null
            : task.project?.id
                !== Number(projectFilter)
        )
      ) {
        return false;
      }

      if (normalizedSearch.length > 0) {
        const searchableText = [
          task.title,
          task.description ?? "",
          task.project?.name ?? "",
        ]
          .join(" ")
          .toLocaleLowerCase("ru-RU");

        if (
          !searchableText.includes(
            normalizedSearch,
          )
        ) {
          return false;
        }
      }

      return true;
    }),
    [
      normalizedSearch,
      priorityFilter,
      projectFilter,
      statusFilter,
      tasks,
    ],
  );

  const activeCount = tasks.filter(
    (task) =>
      task.status !== "completed"
      && task.status !== "failed",
  ).length;

  const completedCount = tasks.filter(
    (task) =>
      task.status === "completed",
  ).length;

  const failedCount = tasks.filter(
    (task) =>
      task.status === "failed",
  ).length;

  const overdueCount = tasks.filter(
    (task) => task.isOverdue,
  ).length;

  return (
    <div className="all-tasks-backdrop">
      <section
        className="all-tasks-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Все задачи"
      >
        <header className="all-tasks-header">
          <div className="all-tasks-title-icon">
            <ListTodo size={24} />
          </div>

          <div className="all-tasks-heading">
            <span>Рабочее пространство</span>
            <h2>Все задачи</h2>

            <p>
              Поиск, фильтрация и управление
              всеми задачами Planer.
            </p>
          </div>

          <div className="all-tasks-header-actions">
            <button
              type="button"
              className="all-tasks-refresh"
              onClick={() => {
                void onRefresh();
              }}
              disabled={isLoading}
            >
              <RefreshCw
                size={18}
                className={
                  isLoading
                    ? "rotating"
                    : ""
                }
              />

              <span>Обновить</span>
            </button>

            <button
              type="button"
              className="all-tasks-create"
              onClick={onCreateTask}
            >
              <Plus size={18} />
              <span>Новая задача</span>
            </button>

            <button
              type="button"
              className="all-tasks-close"
              onClick={onClose}
              aria-label="Закрыть список задач"
            >
              <X size={21} />
            </button>
          </div>
        </header>

        <div className="all-tasks-summary">
          <article>
            <ListTodo size={19} />

            <div>
              <span>Активные</span>
              <strong>{activeCount}</strong>
            </div>
          </article>

          <article>
            <CheckCircle2 size={19} />

            <div>
              <span>Выполненные</span>
              <strong>{completedCount}</strong>
            </div>
          </article>

          <article>
            <CircleX size={19} />

            <div>
              <span>Проваленные</span>
              <strong>{failedCount}</strong>
            </div>
          </article>

          <article>
            <AlertCircle size={19} />

            <div>
              <span>Просроченные</span>
              <strong>{overdueCount}</strong>
            </div>
          </article>
        </div>

        <div className="all-tasks-toolbar">
          <label className="all-tasks-search">
            <Search size={18} />

            <input
              type="search"
              value={searchQuery}
              onChange={(event) => {
                onSearchChange(
                  event.target.value,
                );
              }}
              placeholder="Поиск по задаче, описанию или проекту"
              autoFocus
            />

            {searchQuery.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  onSearchChange("");
                }}
                aria-label="Очистить поиск"
              >
                <X size={16} />
              </button>
            )}
          </label>

          <div className="all-tasks-filters">
            <span className="all-tasks-filter-label">
              <SlidersHorizontal size={16} />
              Фильтры
            </span>

            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(
                  event.target
                    .value as StatusFilter,
                );
              }}
              aria-label="Статус задачи"
            >
              <option value="all">
                Все статусы
              </option>

              {Object.entries(
                STATUS_LABELS,
              ).map(
                ([
                  status,
                  label,
                ]) => (
                  <option
                    key={status}
                    value={status}
                  >
                    {label}
                  </option>
                ),
              )}
            </select>

            <select
              value={priorityFilter}
              onChange={(event) => {
                setPriorityFilter(
                  event.target
                    .value as PriorityFilter,
                );
              }}
              aria-label="Приоритет задачи"
            >
              <option value="all">
                Все приоритеты
              </option>

              {Object.entries(
                PRIORITY_LABELS,
              ).map(
                ([
                  priority,
                  label,
                ]) => (
                  <option
                    key={priority}
                    value={priority}
                  >
                    {label}
                  </option>
                ),
              )}
            </select>

            <select
              value={projectFilter}
              onChange={(event) => {
                setProjectFilter(
                  event.target.value,
                );
              }}
              aria-label="Проект задачи"
            >
              <option value="all">
                Все проекты
              </option>

              <option value="none">
                Без проекта
              </option>

              {projects.map(
                (project) => (
                  <option
                    key={project.id}
                    value={project.id}
                  >
                    {project.name}
                  </option>
                ),
              )}
            </select>
          </div>
        </div>

        {error && (
          <div className="all-tasks-error">
            <AlertCircle size={18} />
            <span>{error}</span>

            <button
              type="button"
              onClick={() => {
                void onRefresh();
              }}
            >
              Повторить
            </button>
          </div>
        )}

        <div className="all-tasks-results-heading">
          <span>
            Найдено задач
          </span>

          <strong>
            {filteredTasks.length}
          </strong>
        </div>

        <div className="all-tasks-list">
          {isLoading && tasks.length === 0 ? (
            <div className="all-tasks-loading">
              <RefreshCw
                size={25}
                className="rotating"
              />

              <span>
                Загружаем задачи…
              </span>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="all-tasks-empty">
              <Search size={30} />

              <strong>
                Задачи не найдены
              </strong>

              <span>
                Измени поиск или параметры
                фильтрации.
              </span>
            </div>
          ) : (
            filteredTasks.map((task) => (
              <article
                key={task.id}
                className={[
                  "all-task-card",
                  task.status
                    === "in_progress"
                    ? "all-task-card-running"
                    : "",
                ].join(" ")}
              >
                <button
                  type="button"
                  className="all-task-main"
                  onClick={() => {
                    onOpenTask(task);
                  }}
                >
                  <div className="all-task-title-row">
                    <div className="all-task-project">
                      <span
                        style={{
                          backgroundColor:
                            task.project?.color
                            ?? "#CBD5E1",
                        }}
                      />

                      <strong>
                        {task.project?.name
                          ?? "Без проекта"}
                      </strong>
                    </div>

                    <span
                      className={[
                        "all-task-status",
                        `all-task-status-${task.status}`,
                      ].join(" ")}
                    >
                      {
                        STATUS_LABELS[
                          task.status
                        ]
                      }
                    </span>
                  </div>

                  <h3>{task.title}</h3>

                  {task.description && (
                    <p>{task.description}</p>
                  )}

                  <div className="all-task-meta">
                    <span>
                      Приоритет:{" "}
                      {
                        PRIORITY_LABELS[
                          task.priority
                        ]
                      }
                    </span>

                    <span>
                      <CalendarClock
                        size={13}
                      />

                      {task.plannedStartAt
                        ? formatSamaraDateTimeShort(
                            task
                              .plannedStartAt,
                          )
                        : "Без планового времени"}
                    </span>

                    <span>
                      <Clock3 size={13} />

                      Факт:{" "}
                      {formatDuration(
                        task
                          .actualDurationSeconds,
                      )}
                    </span>

                    {task.isOverdue && (
                      <span className="all-task-overdue">
                        Просрочена
                      </span>
                    )}
                  </div>
                </button>

                <div className="all-task-actions">
                  <TaskActionButtons
                    task={task}
                    busyAction={
                      taskActionInProgress
                        ?.taskId === task.id
                        ? taskActionInProgress
                            .action
                        : null
                    }
                    onAction={(action) => {
                      void onTaskAction(
                        task,
                        action,
                      );
                    }}
                  />

                  <button
                    type="button"
                    className="all-task-details-button"
                    onClick={() => {
                      onOpenTask(task);
                    }}
                  >
                    Подробнее
                  </button>
                </div>
              </article>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

export default AllTasksPanel;
